// ריטריי לכתיבות שנכשלו מסיבה חולפת — בלי לסכן כפילות.
//
// הרקע: המשתמשת מתעדת ארוחות מהטלפון על 4G, ובקליטה חלשה ה-fetch נכשל באמצע.
// הקוד הציג "השמירה נכשלה — נסי שוב" והיא לחצה שוב ושוב עד שעבר (דיווח 16.09.2026).
// ⚠️ ריטריי עיוור על insert הוא מסוכן: בקשה שהצליחה בשרת ותשובתה אבדה בדרך תיצור
// רישום כפול ביומן. לכן הקוראים מייצרים את ה-id בצד הלקוח, וכפילות מפתח (23505)
// נחשבת הצלחה — הרישום כבר שם. (הגבול המדויק של "נחשבת הצלחה" — ב-entries.addEntries.)

/**
 * ההודעה של השגיאה, גם כשאינה Error.
 * ⚠️ supabase-js לא זורק את ה-TypeError של fetch — הוא עוטף אותו באובייקט פשוט
 * `{ message: 'TypeError: Load failed', details, hint: '', code: '' }` ומחזיר אותו כ-`error` רגיל,
 * ו-entries.ts זורק את האובייקט הזה. ‏`String(obj)` עליו נותן "[object Object]", וזיהוי כשל הרשת —
 * המקרה שבשבילו הקובץ הזה קיים — נכשל בשקט (נתפס בביקורת 16.09.2026; הטסטים המקוריים בדקו
 * TypeError אמיתי בלבד, שדרך supabase-js לא מגיע לכאן לעולם).
 */
function messageOf(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  if (typeof e === 'object' && e !== null) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return '';
}

// נוסחי כשל הרשת של הדפדפנים בפועל — Safari: "Load failed" / "The network connection was lost." /
// "The request timed out." · Chrome: "Failed to fetch" · Firefox: "NetworkError when attempting to fetch
// resource." · Node/undici: "fetch failed" · ביטול (AbortController): "…aborted…" · והסנטינל שלנו: no_session.
// ⚠️ לא `e instanceof TypeError` גורף: כל באג תכנותי הוא TypeError, והוא היה מנוסה שוב ומוצג
// למשתמשת כ"אין חיבור יציב". מסתמכים על הנוסח — ועל status 0 (למטה), שאינו תלוי-נוסח.
const NET_MESSAGE = /no_session|Failed to fetch|Load failed|fetch failed|NetworkError|network (connection|error)|timed? ?out|aborted|Internet connection appears to be offline|Could not connect to the server|hostname could not be found|ECONN|ETIMEDOUT|ENOTFOUND/i;

/** קודי PostgREST/Postgres של תקלה חולפת בצד השרת. הפניה: docs.postgrest.org/en/latest/references/errors.html */
function isTransientCode(code: string): boolean {
  if (/^PGRST00[0-3]$/.test(code)) return true;                  // PostgREST: אין חיבור למסד / pool מלא (503/504)
  if (code === 'PGRST301' || code === 'PGRST303') return true;   // JWT לא תקף / פג (PostgREST ≥12.2 מדווח פקיעה כ-303) — getSession ירענן בניסיון הבא
  if (/^(08|53)/.test(code)) return true;                        // Postgres: connection exception / insufficient resources (PostgREST ממפה ל-503)
  if (code === '40001' || code === '40P01' || code === '40003') return true; // serialization failure / deadlock / statement completion unknown
  if (code === '57014' || /^57P0[123]$/.test(code)) return true; // statement timeout / shutdown / cannot connect now
  return false;
}

/** האם השגיאה חולפת (רשת/סשן/שרת עמוס) ולכן שווה לנסות שוב. */
export function isTransient(e: unknown): boolean {
  if (NET_MESSAGE.test(messageOf(e))) return true;
  if (typeof e !== 'object' || e === null) return false;
  const { code, status } = e as { code?: unknown; status?: unknown };
  if (typeof code === 'string' && isTransientCode(code)) return true;
  // ⚠️ שגיאת PostgREST לא נושאת סטטוס בעצמה (רק message/details/hint/code) — entries.ts מצרף אותו
  // מהתשובה (withStatus). 0 = לא הגיעה תשובת HTTP כלל (כך supabase-js מסמן כשל fetch, בכל דפדפן
  // ובכל נוסח); 408/429 = השרת ביקש לחזור; 5xx = תקלת שרת/שער (Kong/Cloudflare — גוף HTML בלי code).
  if (typeof status === 'number') return status === 0 || status === 408 || status === 429 || status >= 500;
  return false;
}

/** מזהה כשל של מפתח כפול — ריטריי שפגש רישום שכבר נכתב. */
export function isDuplicateKey(e: unknown): boolean {
  const code = (e as { code?: string } | null)?.code;
  return code === '23505';
}

export interface RetryOpts {
  tries?: number;
  baseDelayMs?: number;
}

/**
 * מריץ שוב רק על שגיאה חולפת, עם השהיה גדלה (base·2^i). שגיאה שאינה חולפת (הפרת אילוץ, 4xx)
 * נזרקת מיד — אין טעם לחזור עליה, והמשתמשת צריכה לדעת.
 * ‏`fn` מקבל את מספר הניסיון (0 = השידור הראשון) — כדי שהקורא יוכל להבדיל בין "המזהה כבר שם
 * כי הניסיון הקודם הצליח" לבין כפילות בשידור ראשון, שהיא באג ולא הצלחה.
 */
export async function withRetry<T>(fn: (attempt: number) => Promise<T>, opts: RetryOpts = {}): Promise<T> {
  const tries = Math.max(1, opts.tries ?? 3);
  const base = opts.baseDelayMs ?? 400;
  for (let i = 0; ; i++) {
    try {
      return await fn(i);
    } catch (e) {
      if (!isTransient(e) || i >= tries - 1) throw e;
      await new Promise((r) => setTimeout(r, base * 2 ** i));
    }
  }
}

/**
 * מזהה ייחודי (UUID v4) לשורה חדשה. ‏crypto.randomUUID קיים ב-iOS 15.4+ אבל **רק בהקשר מאובטח** —
 * בדיקה בטלפון מול שרת dev ב-http://<IP> נופלת ל-getRandomValues, שקיים בכל דפדפן. ‏Math.random
 * הוא המפלט האחרון (בלי crypto בכלל): חלש, אבל ייחודי — והשער ב-addEntries (כפילות בשידור ראשון =
 * כשל, לא הצלחה) הופך גם מחולל שבור לשגיאה גלויה במקום לארוחות שנעלמות בשקט.
 */
export function newId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (typeof c?.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  // ⚠️ לא `b.map(...)`: map של TypedArray מחזיר עותק ולא ממלא את b. בגרסה הראשונה של הקובץ כל
  // המזהים במסלול הזה יצאו 00000000-0000-4000-8000-000000000000 — הארוחה הראשונה נשמרת, וכל
  // ארוחה אחריה מקבלת 23505 שנחשב הצלחה: "נוסף ליומן ✓" בלי שום שורה חדשה.
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
